import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import IronSightMark from '../../shared/IronSightMark';
import {
    Chip, Dialog, SegmentedControl, Tooltip,
} from '../../ui';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { MarketPhotosBadge } from '../../InventoryDashboard/UnitDetailPane/CompsPeerPopover';
import apiService from '../../../services/apiService';
import { useCompsSources, useCompsView } from '../../../hooks/useCompsSources';
import { unitDisplayName } from '../../../utils/inventoryView';
import {
    ALL_SOURCES, DATE_RANGES, FOOTNOTE, HILO_VIEW, LIST_PAGE, MARKET_SOURCES, SMALL_SAMPLE, SORT_OPTIONS, SOURCE_META,
    ALL_RANGE, DEFAULT_RANGE, activeFilterKeys, applyFilters, autoRange, dimensionCounts, noMarketData, noMarketReason, olderThanWindow, deltaVsAsk, dominantMeter, filterChips, fmtDay, fmtNum, fmtUsd, fmtUsdShort, insight,
    meterLabel, meterShort, meterUnitWord, newestDate, normalizeOwn, normalizeSource, placeOf, priceStats, regionOf,
    relaxFilter, sortRecords, sourceEmptyText, toggleInList, unitAsk, unitDeltas, unitEstimate, unitMeterValue, unitYear,
    updatedText, zeroResultCulprit,
} from '../../../utils/compsSources';
import CompsScatter, { Swatch } from './CompsScatter';
import './CompsSourcesV2.css';

// The Comps tab's market sources view (docs/decisions/comps-sources.md,
// "Frontend"): filter rail, four source cards (market sold, auction,
// advertised, Hi / Lo / Avg), price over time, and the records of the
// selected source. Every rule lives in utils/compsSources; this file renders.

// ── Source state ─────────────────────────────────────────────────────────

/** loading | error | empty | ok, plus the normalised records. */
function sourceState(key, fetched) {
    if (fetched.status === 'loading') return { kind: 'loading', records: [] };
    if (fetched.status === 'error') return { kind: 'error', records: [] };
    const data = fetched.data || {};
    if (key === 'own') {
        if (data.available === false) return { kind: 'empty', records: [] };
        const records = normalizeOwn(data);
        return { kind: records.length ? 'ok' : 'empty', records };
    }
    if (data.status === 'unavailable') return { kind: 'error', records: [] };
    const records = data.status === 'ok' ? normalizeSource(data, key) : [];
    return { kind: records.length ? 'ok' : 'empty', records };
}

const totalOf = (key, st, fetched) => {
    if (key === 'own') return st.records.length;
    return Math.max(fetched.data?.summary?.total_count_usd || 0, st.records.length);
};

// ── Filter rail ──────────────────────────────────────────────────────────

const toBound = (s) => {
    const t = String(s ?? '').replace(/[,\s]/g, '');
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};

function HoursField({
    label, value, onCommit, placeholder,
}) {
    const [textValue, setTextValue] = useState(value == null ? '' : String(value));
    useEffect(() => { setTextValue(value == null ? '' : String(value)); }, [value]);
    return (
        <input
            className="cs2-in"
            inputMode="numeric"
            aria-label={label}
            placeholder={placeholder}
            value={textValue}
            onChange={(e) => {
                setTextValue(e.target.value);
                onCommit(toBound(e.target.value));
            }}
        />
    );
}
HoursField.propTypes = {
    label: PropTypes.string.isRequired, value: PropTypes.number, onCommit: PropTypes.func.isRequired, placeholder: PropTypes.string,
};

function FilterRail({
    filters, range, autoNote, patch, chooseRange, reset, meter, meterValue, years, subjectYear, regions, anyActive, countsFor,
}) {
    const allYears = years.map((y) => y.year);
    const regionKeys = regions.map((r) => r.key);
    const chosen = filters.regions || [];
    // Only regions the selected source has (with the other filters) can be added.
    const left = regions.filter((r) => !chosen.includes(r.key) && r.count > 0);
    return (
        <aside id="cs2-rail" className="u2-card cs2-rail" aria-label="Filter comparables">
            <div className="cs2-rail-head">
                <h3>Filters</h3>
                <button type="button" className="u2-linkb" onClick={reset} disabled={!anyActive}>Reset all</button>
            </div>

            <div className="cs2-fgroup">
                <span className="u2-eyebrow" id="cs2-f-date">Date range</span>
                <SegmentedControl
                    className="cs2-seg"
                    label="Date range"
                    options={DATE_RANGES.map((r) => ({ value: r.value, label: r.label }))}
                    value={range}
                    onChange={chooseRange}
                />
                {autoNote && <span className="u2-sub cs2-auto" data-testid="auto-range">{autoNote}</span>}
            </div>

            <div className="cs2-fgroup">
                <span className="u2-eyebrow">{meterLabel(meter)}</span>
                <div className="cs2-hours">
                    <HoursField label={`Minimum ${meterLabel(meter).toLowerCase()}`} placeholder="min" value={filters.hMin} onCommit={(hMin) => patch({ hMin })} />
                    <span className="u2-sub" aria-hidden="true">–</span>
                    <HoursField label={`Maximum ${meterLabel(meter).toLowerCase()}`} placeholder="max" value={filters.hMax} onCommit={(hMax) => patch({ hMax })} />
                </div>
                {meterValue != null && (
                    <span className="u2-sub">Yours: {fmtNum(meterValue)} {meterUnitWord(meter)}</span>
                )}
            </div>

            {years.length > 0 && (
                <div className="cs2-fgroup">
                    <span className="u2-eyebrow">Model year</span>
                    <div className="cs2-chips" role="group" aria-label="Model years">
                        {years.map(({ year, count }) => (
                            <button
                                key={year}
                                type="button"
                                className="cs2-chip"
                                aria-pressed={filters.years == null || filters.years.includes(year)}
                                // Nothing of this year in the selected source: nothing to
                                // toggle, unless the user already narrowed the years.
                                disabled={count === 0 && filters.years == null}
                                onClick={() => patch((f) => ({ years: toggleInList(f.years, year, allYears) }))}
                            >
                                {year}
                                {year === subjectYear && <span className="cs2-yours"> · yours</span>}
                                <span className="cs2-chip-n">{count}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {regions.length > 0 && (
                <div className="cs2-fgroup">
                    <span className="u2-eyebrow">Region</span>
                    {chosen.length === 0 ? (
                        <span className="u2-sub">{filters.regions ? 'No regions' : 'All regions'}</span>
                    ) : (
                        <div className="cs2-chips">
                            {chosen.map((key) => (
                                <span key={key} className="cs2-chip cs2-chip--set">
                                    {key}
                                    <button
                                        type="button"
                                        className="cs2-x"
                                        aria-label={`Remove region ${key}`}
                                        onClick={() => patch((f) => {
                                            const next = (f.regions || []).filter((r) => r !== key);
                                            return { regions: next.length ? next : null };
                                        })}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    {left.length > 0 && (
                        <select
                            className="cs2-in cs2-sel"
                            aria-label="Add a region"
                            value=""
                            onChange={(e) => {
                                const key = e.target.value;
                                if (!key) return;
                                patch((f) => {
                                    const next = [...(f.regions || []), key];
                                    return { regions: regionKeys.every((k) => next.includes(k)) ? null : next };
                                });
                            }}
                        >
                            <option value="">{chosen.length ? 'Add a region…' : 'Only show a region…'}</option>
                            {left.map((r) => <option key={r.key} value={r.key}>{r.key} ({r.count})</option>)}
                        </select>
                    )}
                </div>
            )}

            <p className="u2-sub cs2-rail-note">
                Apply to market sold, auction and advertised alike. US-dollar results only.
                {meter !== 'hours' && ` Your sales carry engine hours, so the ${meterLabel(meter).toLowerCase()} filter skips them.`}
                {' '}Regions apply to the market feed only. Counts on the chips are for {countsFor}.
            </p>
        </aside>
    );
}
FilterRail.propTypes = {
    filters: PropTypes.object.isRequired,
    range: PropTypes.string.isRequired,
    autoNote: PropTypes.string,
    chooseRange: PropTypes.func.isRequired,
    patch: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    meter: PropTypes.string.isRequired,
    meterValue: PropTypes.number,
    years: PropTypes.array.isRequired,
    subjectYear: PropTypes.number,
    regions: PropTypes.array.isRequired,
    anyActive: PropTypes.bool,
    countsFor: PropTypes.string,
};

// ── Source cards (tabs) ──────────────────────────────────────────────────

function cardSub(key, filtered, st) {
    const n = priceStats(filtered).n;
    if (st.kind === 'empty') return 'None for this model';
    if (n > 0 && n < SMALL_SAMPLE) return `Only ${n} ${n === 1 ? SOURCE_META[key].noun : SOURCE_META[key].nouns} · treat as indicative`;
    const newest = newestDate(filtered);
    if (key === 'advertised') return `Asking prices${newest ? ` · newest ${fmtDay(newest)}` : ''}`;
    if (key === 'auction') return newest ? `Newest auction ${fmtDay(newest)}` : 'No auctions in this view';
    return newest ? `Newest sale ${fmtDay(newest)}` : 'No sales in this view';
}

function SourceCard({
    id, sourceKey, selected, onSelect, st, fetched, filtered, onKeyNav, btnRef,
}) {
    const meta = SOURCE_META[sourceKey];
    const stats = priceStats(filtered);
    const small = stats.n > 0 && stats.n < SMALL_SAMPLE;
    let body;
    if (st.kind === 'loading') {
        body = (
            <span className="cs2-card-skel" aria-hidden="true">
                <span className="v2-sk" style={{ width: '55%' }} />
                <span className="v2-sk" style={{ width: '75%', height: 16 }} />
                <span className="v2-sk" style={{ width: '65%' }} />
            </span>
        );
    } else if (st.kind === 'error') {
        body = <span className="cs2-card-sub cs2-card-err">{meta.label} prices didn&apos;t load.</span>;
    } else {
        body = (
            <>
                <span className={`cs2-card-n${small ? ' is-small' : ''}`}>
                    <b>{fmtNum(filtered.length)}</b> of {fmtNum(totalOf(sourceKey, st, fetched))}
                </span>
                <span className={`cs2-card-med${small ? ' is-small' : ''}`}>
                    {stats.median != null ? <><b>{fmtUsd(stats.median)}</b> median</> : <span className="u2-sub">no median</span>}
                </span>
                <span className="cs2-card-sub">{cardSub(sourceKey, filtered, st)}</span>
                {fetched.data?.stale && <span className="cs2-card-sub cs2-stale">{updatedText(fetched.data.as_of)}</span>}
            </>
        );
    }
    return (
        <div className={`cs2-card${selected ? ' is-on' : ''}`} data-testid={`card-${sourceKey}`}>
            <button
                id={id}
                ref={btnRef}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="cs2-panel"
                tabIndex={selected ? 0 : -1}
                className="cs2-card-btn"
                onClick={onSelect}
                onKeyDown={onKeyNav}
                aria-busy={st.kind === 'loading' || undefined}
            >
                <span className="cs2-card-l"><Swatch source={sourceKey} />{meta.label}</span>
                {body}
            </button>
            {meta.hint && (
                <span className={`cs2-card-hint${selected ? ' is-on' : ''}`}>
                    <Tooltip content={meta.hint} label={`About ${meta.label}`} />
                </span>
            )}
            {st.kind === 'error' && (
                <button type="button" className="v2-btn v2-btn--default v2-btn--sm cs2-card-retry" onClick={fetched.retry}>
                    Retry {meta.label.toLowerCase()}
                </button>
            )}
        </div>
    );
}
SourceCard.propTypes = {
    id: PropTypes.string.isRequired,
    sourceKey: PropTypes.string.isRequired,
    selected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    st: PropTypes.object.isRequired,
    fetched: PropTypes.object.isRequired,
    filtered: PropTypes.array.isRequired,
    onKeyNav: PropTypes.func.isRequired,
    btnRef: PropTypes.func,
};

// ── Record list ──────────────────────────────────────────────────────────

function Thumb({
    stock, idx, buildId, title, openable,
}) {
    const ref = useRef(null);
    const [url, setUrl] = useState(null);
    const [open, setOpen] = useState(false);
    const [want, setWant] = useState(typeof IntersectionObserver === 'undefined');
    useEffect(() => {
        if (want || !ref.current) return undefined;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) { setWant(true); io.disconnect(); }
        }, { rootMargin: '200px' });
        io.observe(ref.current);
        return () => io.disconnect();
    }, [want]);
    useEffect(() => {
        if (!want) return undefined;
        let live = true;
        let made = null;
        const controller = new AbortController();
        apiService.getMarketCompsPhoto(stock, idx, 0, buildId, { signal: controller.signal })
            .then((blob) => {
                if (!live || !(typeof Blob !== 'undefined' && blob instanceof Blob)) return;
                made = URL.createObjectURL(blob);
                setUrl(made);
            })
            .catch(() => {});
        return () => {
            live = false;
            controller.abort();
            if (made) URL.revokeObjectURL(made);
        };
    }, [want, stock, idx, buildId]);
    const img = url ? <img src={url} alt="" onError={() => setUrl(null)} /> : <span className="cs2-thumb-ph" />;
    if (!openable) return <span ref={ref} className="cs2-thumb" aria-hidden="true">{img}</span>;
    // One photo: the thumbnail itself opens it (no gallery popover for one).
    return (
        <>
            <button
                ref={ref}
                type="button"
                className="cs2-thumb cs2-thumb--btn"
                aria-label={`Open the listing photo of ${title}`}
                disabled={!url}
                onClick={() => setOpen(true)}
            >
                {img}
            </button>
            <Dialog open={open} onClose={() => setOpen(false)} title={title} wide>
                {url && <img className="cs2-photo-big" src={url} alt={`Listing: ${title}`} />}
            </Dialog>
        </>
    );
}
Thumb.propTypes = {
    stock: PropTypes.string.isRequired, idx: PropTypes.number.isRequired, buildId: PropTypes.string, title: PropTypes.string, openable: PropTypes.bool,
};

function RecordCard({
    r, unitName, ask, unitCtx, meter, selected, onSelect, stock, buildId,
}) {
    const meta = SOURCE_META[r.source];
    const delta = deltaVsAsk(r.price, ask);
    const deltas = unitDeltas(r, unitCtx);
    const place = placeOf(r);
    const title = [r.year, unitName].filter(Boolean).join(' ');
    const hours = r.hours != null ? `${fmtNum(r.hours)} ${meterLabel(r.meterUnit || meter).toLowerCase()}` : `${meterLabel(r.meterUnit || meter)}: not given`;
    return (
        <li
            id={`cs2-rec-${r.id}`}
            className={`cs2-rec${selected ? ' is-sel' : ''}`}
            data-testid="comps-record"
        >
            {r.source === 'advertised' && r.photoCount > 0 && (
                <Thumb stock={stock} idx={r.idx} buildId={buildId} title={title} openable={r.photoCount === 1} />
            )}
            <div className="cs2-rec-main">
                <button type="button" className="cs2-rec-title" aria-pressed={selected} onClick={() => onSelect(selected ? null : r.id)}>
                    {title}
                </button>
                <div className="cs2-rec-meta">
                    <span>{hours}</span>
                    {place && <span>{place}</span>}
                    <span>{meta.dateLabel} {fmtDay(r.t)}</span>
                </div>
                {deltas && <div className="cs2-rec-delta">{deltas}</div>}
                {r.options && <p className="cs2-rec-opts" title={r.options}>{r.options}</p>}
                {r.source === 'advertised' && r.photoCount > 1 && (
                    <MarketPhotosBadge
                        stockNumber={stock}
                        listingIndex={r.idx}
                        count={r.photoCount}
                        badgeClass="market-photos-neutral"
                        buildId={buildId || undefined}
                    />
                )}
            </div>
            <div className="cs2-rec-price">
                <b>{fmtUsd(r.price)}</b>
                <span className="cs2-rec-word">{meta.priceWord}</span>
                {delta && <span className={`cs2-rec-vs cs2-rec-vs--${delta.tone}`}>{delta.text}</span>}
            </div>
        </li>
    );
}
RecordCard.propTypes = {
    r: PropTypes.object.isRequired,
    unitName: PropTypes.string,
    ask: PropTypes.number,
    unitCtx: PropTypes.object.isRequired,
    meter: PropTypes.string.isRequired,
    selected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    stock: PropTypes.string.isRequired,
    buildId: PropTypes.string,
};

function ZeroResults({
    noun, culprit, onRelax, onReset, older, olderNoun,
}) {
    if (older) {
        return (
            <div className="cs2-zero" role="status">
                <p className="u2-note">
                    <b>None in this date range.</b> The newest {olderNoun} is from {fmtDay(older.newest)}, older than this window.
                    {' '}
                    <button type="button" className="u2-linkb" onClick={() => onRelax('range')}>Show all dates</button>
                </p>
            </div>
        );
    }
    return (
        <div className="cs2-zero" role="status">
            <p className="u2-note"><b>No {noun} match these filters.</b></p>
            {culprit ? (
                <p className="u2-note">
                    {culprit.label.charAt(0).toUpperCase() + culprit.label.slice(1)} removed{culprit.all ? ' all' : ''} {fmtNum(culprit.gain)} of them.
                    {' '}
                    <button type="button" className="u2-linkb" onClick={() => onRelax(culprit.key)}>Clear {culprit.label}</button>
                </p>
            ) : (
                <p className="u2-note">
                    No single filter brings any back.
                    {' '}
                    <button type="button" className="u2-linkb" onClick={onReset}>Reset all filters</button>
                </p>
            )}
        </div>
    );
}
ZeroResults.propTypes = {
    older: PropTypes.object,
    olderNoun: PropTypes.string,
    noun: PropTypes.string.isRequired, culprit: PropTypes.object, onRelax: PropTypes.func.isRequired, onReset: PropTypes.func.isRequired,
};

function RecordList({
    sourceKey, st, fetched, filtered, sort, setSort, showAll, setShowAll, culprit, older, onRelax, onReset, ...cardProps
}) {
    const meta = SOURCE_META[sourceKey];
    if (st.kind === 'loading') {
        return (
            <div className="u2-card cs2-list cs2-list--loading" role="status" aria-label={`Loading ${meta.nouns}`}>
                <span className="v2-sk" style={{ width: '30%' }} />
                {[0, 1, 2].map((i) => <span key={i} className="cs2-rec-skel v2-sk" />)}
            </div>
        );
    }
    if (st.kind === 'error') {
        return (
            <div className="u2-card cs2-list" role="alert">
                <p className="u2-note"><b>{meta.label} could not be loaded.</b> The other sources are not affected.</p>
                <button type="button" className="v2-btn v2-btn--primary v2-btn--sm cs2-retry" onClick={fetched.retry}>Retry</button>
            </div>
        );
    }
    if (st.kind === 'empty') {
        return (
            <div className="u2-card cs2-list">
                <p className="u2-note">{sourceEmptyText(sourceKey, fetched.data)}</p>
            </div>
        );
    }
    const sorted = sortRecords(filtered, sort);
    const shown = showAll ? sorted : sorted.slice(0, LIST_PAGE);
    const total = totalOf(sourceKey, st, fetched);
    return (
        <div className="u2-card cs2-list">
            <div className="u2-c-head">
                <h3>{meta.label}</h3>
                <span className="u2-sub">
                    {fmtNum(filtered.length)} of {fmtNum(total)} {meta.nouns}
                    {sourceKey === 'advertised' ? ' · asking prices' : ''}
                </span>
                <span className="u2-right">
                    <select className="cs2-in cs2-sel" aria-label="Sort records" value={sort} onChange={(e) => setSort(e.target.value)}>
                        {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </span>
            </div>
            {fetched.data?.truncated && (
                <p className="u2-sub">The newest {fmtNum(st.records.length)} of {fmtNum(total)} are loaded; filters work on those.</p>
            )}
            {filtered.length === 0 ? (
                <ZeroResults noun={meta.nouns} culprit={culprit} older={older} olderNoun={meta.noun} onRelax={onRelax} onReset={onReset} />
            ) : (
                <ul className="cs2-recs">
                    {shown.map((r) => (
                        <RecordCard key={r.id} r={r} selected={cardProps.selectedId === r.id} {...cardProps} />
                    ))}
                </ul>
            )}
            {!showAll && sorted.length > LIST_PAGE && (
                <button type="button" className="u2-linkb" onClick={() => setShowAll(true)}>Show all {fmtNum(sorted.length)}</button>
            )}
        </div>
    );
}
RecordList.propTypes = {
    sourceKey: PropTypes.string.isRequired,
    st: PropTypes.object.isRequired,
    fetched: PropTypes.object.isRequired,
    filtered: PropTypes.array.isRequired,
    sort: PropTypes.string.isRequired,
    setSort: PropTypes.func.isRequired,
    showAll: PropTypes.bool,
    setShowAll: PropTypes.func.isRequired,
    culprit: PropTypes.object,
    older: PropTypes.object,
    onRelax: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    selectedId: PropTypes.string,
};

// ── Hi / Lo / Avg ────────────────────────────────────────────────────────

function RangeBar({ s, lo, hi, ask }) {
    const pos = (v) => `${(((v - lo) / (hi - lo || 1)) * 100).toFixed(2)}%`;
    if (s.n === 0) return <span className="u2-quiet">—</span>;
    return (
        <span
            className="cs2-rb"
            role="img"
            aria-label={`Range ${fmtUsd(s.min)} to ${fmtUsd(s.max)}, middle half ${fmtUsd(s.p25)} to ${fmtUsd(s.p75)}, median ${fmtUsd(s.median)}`}
        >
            <span className="cs2-rb-mm" style={{ left: pos(s.min), right: `calc(100% - ${pos(s.max)})` }} />
            <span className="cs2-rb-iq" style={{ left: pos(s.p25), right: `calc(100% - ${pos(s.p75)})` }} />
            <span className="cs2-rb-md" style={{ left: pos(s.median) }} />
            {ask != null && <span className="cs2-rb-ask" style={{ left: pos(Math.min(Math.max(ask, lo), hi)) }} />}
        </span>
    );
}
RangeBar.propTypes = {
    s: PropTypes.object.isRequired, lo: PropTypes.number.isRequired, hi: PropTypes.number.isRequired, ask: PropTypes.number,
};

function HiLoTable({
    states, fetchedAll, filteredAll, ask,
}) {
    const rows = ALL_SOURCES.map((key) => ({ key, st: states[key], s: priceStats(filteredAll[key]) }));
    const vals = rows.flatMap((r) => (r.s.n ? [r.s.min, r.s.max] : []));
    if (ask != null) vals.push(ask);
    const lo = vals.length ? Math.min(...vals) : 0;
    const hi = vals.length ? Math.max(...vals) : 1;
    return (
        <div className="u2-card cs2-hilo" id="cs2-panel" role="tabpanel" aria-labelledby="cs2-tab-hilo">
            <div className="u2-c-head">
                <h3>Hi / Lo / Avg</h3>
                <span className="u2-sub">every source over the same filters · median first, average for reference</span>
            </div>
            <div className="u2-tw">
                <table className="u2-tt cs2-hilo-t" aria-label="Prices by source">
                    <thead>
                        <tr>
                            <th>Source</th>
                            <th className="r">Count</th>
                            <th className="r">Low</th>
                            <th className="r">Median</th>
                            <th className="r">Average</th>
                            <th className="r">High</th>
                            <th className="cs2-rb-h">Range · middle half · your ask</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ key, st, s }) => {
                            const meta = SOURCE_META[key];
                            const name = (
                                <span className="cs2-src-n">
                                    <Swatch source={key} />
                                    {meta.label}
                                    {meta.hint && <Tooltip content={meta.hint} label={`About ${meta.label}`} />}
                                </span>
                            );
                            if (st.kind !== 'ok') {
                                let msg = 'Loading…';
                                if (st.kind === 'error') msg = 'Could not load';
                                if (st.kind === 'empty') msg = sourceEmptyText(key, fetchedAll[key].data);
                                return (
                                    <tr key={key} className="is-dim">
                                        <td>{name}</td>
                                        <td colSpan={6} className="cs2-hilo-msg">
                                            {msg}
                                            {st.kind === 'error' && (
                                                <>
                                                    {' '}
                                                    <button type="button" className="u2-linkb" onClick={fetchedAll[key].retry}>Retry</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            }
                            const small = s.n > 0 && s.n < SMALL_SAMPLE;
                            return (
                                <tr key={key} className={small ? 'is-dim' : undefined}>
                                    <td>
                                        {name}
                                        {small && <span className="cs2-small">small sample</span>}
                                    </td>
                                    <td className="r">{fmtNum(s.n)}</td>
                                    <td className="r">{fmtUsd(s.min)}</td>
                                    <td className="r"><b>{fmtUsd(s.median)}</b></td>
                                    <td className="r">{fmtUsd(s.mean)}</td>
                                    <td className="r">{fmtUsd(s.max)}</td>
                                    <td className="cs2-rb-cell"><RangeBar s={s} lo={lo} hi={hi} ask={ask} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="u2-legend cs2-rb-legend" aria-hidden="true">
                <span><i className="cs2-lg-mm" />Low to high</span>
                <span><i className="cs2-lg-iq" />Middle half (25th–75th percentile)</span>
                <span><i className="cs2-lg-md" />Median</span>
                {ask != null && <span><i className="cs2-lg-ask" />Your ask {fmtUsdShort(ask)}</span>}
            </div>
        </div>
    );
}
HiLoTable.propTypes = {
    states: PropTypes.object.isRequired, fetchedAll: PropTypes.object.isRequired, filteredAll: PropTypes.object.isRequired, ask: PropTypes.number,
};

// ── No market data ───────────────────────────────────────────────────────

function NoMarketPanel({
    reason, own, ownFetched, ask,
}) {
    const sales = sortRecords(own.records, 'recent');
    const s = priceStats(sales);
    return (
        <div className="u2-card cs2-nomarket" data-testid="no-market">
            <h3>No market data for this model yet</h3>
            <p className="u2-note">{reason} Market sold, auction and advertised prices will show here once the market feed has them.</p>
            <div className="cs2-nomarket-own">
                <h4><Swatch source="own" />Your sales <Tooltip content={SOURCE_META.own.hint} label="About Your sales" /></h4>
                {own.kind === 'loading' && <span className="v2-sk" style={{ width: '40%' }} />}
                {own.kind === 'error' && (
                    <p className="u2-note">
                        Your sales didn&apos;t load.
                        {' '}
                        <button type="button" className="u2-linkb" onClick={ownFetched.retry}>Retry</button>
                    </p>
                )}
                {own.kind === 'empty' && <p className="u2-note">{sourceEmptyText('own', ownFetched.data)}</p>}
                {own.kind === 'ok' && (
                    <>
                        <p className="u2-note">
                            <b>{fmtNum(s.n)}</b> sale{s.n === 1 ? '' : 's'} since {fmtDay(sales[sales.length - 1]?.t)}, median <b>{fmtUsd(s.median)}</b>
                            {s.n < SMALL_SAMPLE && ' (small sample: indicative only)'}.
                        </p>
                        <div className="u2-tw">
                            <table className="u2-tt" aria-label="Your sales of this model">
                                <thead>
                                    <tr>
                                        <th>Sold on</th>
                                        <th className="r">Year</th>
                                        <th className="r">Price</th>
                                        <th className="r">vs your ask</th>
                                        <th className="r">Engine hours</th>
                                        <th>Branch</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map((r) => (
                                        <tr key={r.id}>
                                            <td>{fmtDay(r.t)}</td>
                                            <td className="r">{r.year ?? '—'}</td>
                                            <td className="r">{fmtUsd(r.price)}</td>
                                            <td className="r">{deltaVsAsk(r.price, ask)?.text.replace(' vs your ask', '') ?? '—'}</td>
                                            <td className="r">{fmtNum(r.hours)}</td>
                                            <td>{r.branch || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
NoMarketPanel.propTypes = {
    reason: PropTypes.string.isRequired, own: PropTypes.object.isRequired, ownFetched: PropTypes.object.isRequired, ask: PropTypes.number,
};

// ── How to read this ─────────────────────────────────────────────────────

function HowToRead() {
    return (
        <details className="cs2-how">
            <summary>How to read this</summary>
            <ul>
                <li>Market sold are sale prices dealers across the US reported to the market feed, auction are hammer prices, advertised are asking prices of machines still for sale. Your sales are units your dealership sold (used, over $1,000), net of trade-in over-allowance.</li>
                <li>All four use model years around this unit. The filters on the left apply to every source at once; &quot;x of y&quot; on a card is how many of its records are left.</li>
                <li>The median is the headline: half the prices are below it. The average is shown for reference; one unusual price moves it more.</li>
                <li>Under {SMALL_SAMPLE} records a figure is greyed out and marked as indicative.</li>
                <li>Above / below market compares your ask with the middle half (25th to 75th percentile) of the selected source in this view.</li>
                <li>The chart keeps a few extreme prices off the scale so the rest stay readable; they are pinned to the edge and counted.</li>
            </ul>
        </details>
    );
}

// ── The section ──────────────────────────────────────────────────────────

const VIEW_KEYS = [...MARKET_SOURCES, HILO_VIEW];

export default function CompsSourcesV2({ unit, machineCatalog }) {
    const stock = String(unit.StockNumber);
    const fetchedAll = useCompsSources(stock);
    const {
        view, setView, sort, setSort, filters, deferredFilters, setFilters, patchFilters, resetFilters, rangeChosen, chooseRange,
        applyAutoRange, autoShown,
    } = useCompsView(stock);
    const narrow = useMediaQuery('(max-width: 720px)');
    const [railOpen, setRailOpen] = useState(false);
    // "Today" once per mount: date windows stay put while the user works.
    const [now] = useState(() => Date.now());
    const [selectedId, setSelectedId] = useState(null);
    // Legend overrides of the default series (the selected source + your
    // sales). A newly selected card is always drawn; the other choices stay.
    const [seriesOverride, setSeriesOverride] = useState({});
    useEffect(() => {
        setSeriesOverride((o) => {
            if (!(view in o)) return o;
            const n = { ...o };
            delete n[view];
            return n;
        });
    }, [view]);
    const [showAll, setShowAll] = useState(false);
    const tabRefs = useRef({});

    const states = useMemo(() => {
        const out = {};
        ALL_SOURCES.forEach((k) => { out[k] = sourceState(k, fetchedAll[k]); });
        return out;
    }, [fetchedAll]);

    const marketAll = useMemo(() => MARKET_SOURCES.flatMap((k) => states[k].records), [states]);
    const meter = useMemo(() => dominantMeter(marketAll), [marketAll]);
    const ctx = useMemo(() => ({ now, meter }), [now, meter]);
    const refSource = MARKET_SOURCES.includes(view) ? view : 'sold';
    // The default 12 months empty for the selected source but older records
    // exist: show All instead, and say so.
    // (When the selected source itself failed, the other market sources decide.)
    // Decided by the selected source; if that one failed or is empty, by the
    // others once none is still loading.
    const marketLoading = MARKET_SOURCES.some((k) => states[k].kind === 'loading');
    let autoBasis = [];
    if (states[refSource].kind === 'ok') autoBasis = states[refSource].records;
    else if (states[refSource].kind !== 'loading' && !marketLoading) autoBasis = marketAll;
    // Decided on the date alone and applied once (kept afterwards).
    const auto = autoRange(autoBasis, filters, ctx, rangeChosen);
    useEffect(() => { if (auto) applyAutoRange(); }, [auto, applyAutoRange]);
    // Until the date fallback is decided (the selected source, or the others
    // when it failed, still loading; or the fallback found but not yet
    // applied), the 12-month figures would flash first: show every loaded
    // source as loading instead. Errors stay visible.
    const autoPending = !rangeChosen && filters.range === DEFAULT_RANGE && (
        Boolean(auto)
        || states[refSource].kind === 'loading'
        || (states[refSource].kind !== 'ok' && marketLoading)
    );
    const shownStates = useMemo(() => {
        if (!autoPending) return states;
        const out = {};
        ALL_SOURCES.forEach((k) => {
            out[k] = states[k].kind === 'error' ? states[k] : { kind: 'loading', records: [] };
        });
        return out;
    }, [autoPending, states]);
    const eff = deferredFilters;
    const filteredAll = useMemo(() => {
        const out = {};
        ALL_SOURCES.forEach((k) => { out[k] = applyFilters(states[k].records, eff, ctx); });
        return out;
    }, [states, eff, ctx]);
    const older = useMemo(
        () => (states[refSource].kind === 'ok' ? olderThanWindow(states[refSource].records, eff, ctx) : null),
        [states, refSource, eff, ctx],
    );

    const ask = unitAsk(unit);
    const estimate = unitEstimate(unit);
    const subjectYear = unitYear(unit);
    const meterValue = unitMeterValue(unit, meter);
    const unitName = unitDisplayName(unit, machineCatalog).name;
    const unitCtx = useMemo(() => ({ year: subjectYear, meterValue, meter }), [subjectYear, meterValue, meter]);

    // Chip counts: the selected source, with every other filter applied (so
    // they match its card); the window's years and every region seen stay
    // listed, at 0 when the source has none.
    const years = useMemo(() => {
        const counts = dimensionCounts(states[refSource].records, eff, ctx, 'years', (r) => r.year);
        const all = new Set(counts.keys());
        MARKET_SOURCES.forEach((k) => (fetchedAll[k].data?.window?.years || []).forEach((y) => all.add(y)));
        marketAll.forEach((r) => { if (r.year != null) all.add(r.year); });
        return [...all].sort((a, b) => a - b).map((year) => ({ year, count: counts.get(year) || 0 }));
    }, [states, refSource, eff, ctx, fetchedAll, marketAll]);
    const regions = useMemo(() => {
        const counts = dimensionCounts(states[refSource].records, eff, ctx, 'regions', regionOf);
        const all = new Set(counts.keys());
        marketAll.forEach((r) => { const k = regionOf(r); if (k) all.add(k); });
        return [...all].map((key) => ({ key, count: counts.get(key) || 0 }))
            .sort((a, b) => b.count - a.count || (a.key < b.key ? -1 : 1));
    }, [states, refSource, eff, ctx, marketAll]);

    // The automatic All is not the user's filter: no chip, no "Reset all".
    const userFilters = autoShown ? { ...filters, range: DEFAULT_RANGE } : filters;
    const active = activeFilterKeys(userFilters);
    const chips = filterChips(userFilters, meter);
    const autoNote = autoShown && autoBasis.length
        ? `No ${states[refSource].kind === 'ok' ? SOURCE_META[refSource].nouns : 'market records'} in the last 12 months (newest ${fmtDay(newestDate(autoBasis))}), so all dates are shown.`
        : null;
    const info = insight({
        ask, refSource, refRecords: filteredAll[refSource], ownRecords: filteredAll.own,
    });
    const culprit = useMemo(() => {
        if (!MARKET_SOURCES.includes(view) || filteredAll[view].length !== 0) return null;
        const c = zeroResultCulprit(states[view].records, eff, ctx);
        if (!c) return null;
        const label = c.key === 'hours' ? meterLabel(meter).toLowerCase() : c.label;
        return { ...c, label, all: c.gain === states[view].records.length };
    }, [view, filteredAll, states, eff, ctx, meter]);

    const shown = (k) => seriesOverride[k] ?? (k === refSource || k === 'own');
    const series = ALL_SOURCES
        .filter((k) => shownStates[k].kind === 'ok' && shown(k))
        .map((k) => ({ source: k, records: filteredAll[k] }));
    const chartLabel = `Price over time: ${ALL_SOURCES.filter((k) => shownStates[k].kind === 'ok')
        .map((k) => `${filteredAll[k].length} ${SOURCE_META[k].label.toLowerCase()}`).join(', ')}. ${info.sentence}`;

    // Changing the source or the filters starts the list at the top again.
    useEffect(() => { setShowAll(false); }, [view, eff, sort]);

    const byId = useMemo(() => {
        const m = new Map();
        ALL_SOURCES.forEach((k) => states[k].records.forEach((r) => m.set(r.id, r)));
        return m;
    }, [states]);

    // Selecting a point never changes the selected card or the legend: it
    // highlights (and on a click scrolls to) its record card when that
    // source's list is the one shown; otherwise the read-out is all.
    const onSelect = useCallback((id, { via } = {}) => {
        setSelectedId(id);
        const r = id ? byId.get(id) : null;
        if (!r || r.source !== view) return;
        const pos = sortRecords(filteredAll[r.source], sort).findIndex((x) => x.id === id);
        if (pos >= LIST_PAGE) setTimeout(() => setShowAll(true), 0);
        if (via === 'pointer') {
            setTimeout(() => {
                const el = document.getElementById(`cs2-rec-${id}`);
                if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }, 50);
        }
    }, [byId, view, filteredAll, sort]);

    const onKeyNav = (e) => {
        const i = VIEW_KEYS.indexOf(view);
        let next = null;
        if (e.key === 'ArrowRight') next = VIEW_KEYS[(i + 1) % VIEW_KEYS.length];
        else if (e.key === 'ArrowLeft') next = VIEW_KEYS[(i - 1 + VIEW_KEYS.length) % VIEW_KEYS.length];
        else if (e.key === 'Home') [next] = VIEW_KEYS;
        else if (e.key === 'End') next = VIEW_KEYS[VIEW_KEYS.length - 1];
        if (!next) return;
        e.preventDefault();
        setView(next);
        tabRefs.current[next]?.focus();
    };

    const toggleSeries = (k) => setSeriesOverride((o) => ({ ...o, [k]: !shown(k) }));

    const windowYears = years.length ? `${years[0].year}–${years[years.length - 1].year}` : null;
    const anyLoading = ALL_SOURCES.some((k) => shownStates[k].kind === 'loading');
    const head = (
        <div className="cs2-head">
            <span className="u2-is-mark"><IronSightMark /> IronSight</span>
            <h3>Market comparables</h3>
            <span className="u2-sub">
                {[unitName, windowYears && `model years ${windowYears}`, 'US dollars'].filter(Boolean).join(' · ')}
            </span>
        </div>
    );

    // No market source has anything for this unit: one panel, your sales only.
    const payloads = { sold: fetchedAll.sold.data, auction: fetchedAll.auction.data, advertised: fetchedAll.advertised.data };
    if (MARKET_SOURCES.every((k) => states[k].kind === 'empty') && noMarketData(payloads)) {
        return (
            <section className="cs2" aria-label="Market comparables">
                {head}
                <NoMarketPanel reason={noMarketReason(payloads, unit)} own={states.own} ownFetched={fetchedAll.own} ask={ask} />
                <p className="u2-sub cs2-foot">{FOOTNOTE}</p>
            </section>
        );
    }

    const rail = (
        <FilterRail
            filters={filters}
            range={filters.range}
            autoNote={autoNote}
            patch={patchFilters}
            chooseRange={chooseRange}
            reset={resetFilters}
            meter={meter}
            meterValue={meterValue}
            years={years}
            subjectYear={subjectYear}
            regions={regions}
            anyActive={active.length > 0}
            countsFor={SOURCE_META[refSource].label.toLowerCase()}
        />
    );

    return (
        <section className="cs2" aria-label="Market comparables">
            {head}
            <div className="cs2-layout">
                {narrow ? (
                    <div className="cs2-rail-narrow">
                        <button
                            type="button"
                            className="v2-btn v2-btn--default cs2-rail-toggle"
                            aria-expanded={railOpen}
                            aria-controls="cs2-rail"
                            onClick={() => setRailOpen((o) => !o)}
                        >
                            {railOpen ? 'Hide filters' : 'Filters'}
                            {active.length > 0 && ` (${active.length} active)`}
                        </button>
                        {railOpen && rail}
                    </div>
                ) : rail}
                <div className="cs2-main">
                    {chips.length > 0 && (
                        <div className="cs2-applied" aria-label="Applied filters">
                            <span className="u2-sub">Filtered:</span>
                            {chips.map((c) => (
                                <span key={c.key} className="cs2-chip cs2-chip--set">
                                    {c.label}
                                    <button
                                        type="button"
                                        className="cs2-x"
                                        aria-label={`Clear ${c.label}`}
                                        onClick={() => setFilters((f) => relaxFilter(f, c.key))}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            <button type="button" className="u2-linkb" onClick={resetFilters}>Reset all</button>
                        </div>
                    )}
                    <div className="cs2-cards" role="tablist" aria-label="Comparable sources">
                        {MARKET_SOURCES.map((k) => (
                            <SourceCard
                                key={k}
                                id={`cs2-tab-${k}`}
                                sourceKey={k}
                                selected={view === k}
                                onSelect={() => setView(k)}
                                st={shownStates[k]}
                                fetched={fetchedAll[k]}
                                filtered={filteredAll[k]}
                                onKeyNav={onKeyNav}
                                btnRef={(el) => { tabRefs.current[k] = el; }}
                            />
                        ))}
                        <div className={`cs2-card cs2-card--hilo${view === HILO_VIEW ? ' is-on' : ''}`} data-testid="card-hilo">
                            <button
                                id="cs2-tab-hilo"
                                ref={(el) => { tabRefs.current[HILO_VIEW] = el; }}
                                type="button"
                                role="tab"
                                aria-selected={view === HILO_VIEW}
                                aria-controls="cs2-panel"
                                tabIndex={view === HILO_VIEW ? 0 : -1}
                                className="cs2-card-btn"
                                onClick={() => setView(HILO_VIEW)}
                                onKeyDown={onKeyNav}
                            >
                                <span className="cs2-card-l">Hi / Lo / Avg</span>
                                <span className="cs2-card-med"><b>Compare sources</b></span>
                                <span className="cs2-card-sub">Low, median, average and high side by side, your sales included</span>
                            </button>
                        </div>
                    </div>

                    {view === HILO_VIEW ? (
                        <HiLoTable states={shownStates} fetchedAll={fetchedAll} filteredAll={filteredAll} ask={ask} />
                    ) : (
                        <div id="cs2-panel" role="tabpanel" aria-labelledby={`cs2-tab-${view}`} className="cs2-panel">
                            <div className="u2-card cs2-chart-card">
                                <div className="u2-c-head">
                                    <h3>Price over time</h3>
                                    <span className="u2-sub">{DATE_RANGES.find((r) => r.value === eff.range)?.short} · {narrow ? 'tap a point for details' : 'tap a point, or focus the chart and use ← →'}</span>
                                </div>
                                <div className="cs2-legend" role="group" aria-label="Show or hide sources">
                                    {ALL_SOURCES.map((k) => {
                                        const st = shownStates[k];
                                        return (
                                            <button
                                                key={k}
                                                type="button"
                                                className="cs2-lg"
                                                aria-pressed={st.kind === 'ok' && shown(k)}
                                                disabled={st.kind !== 'ok'}
                                                title={SOURCE_META[k].hint}
                                                onClick={() => toggleSeries(k)}
                                            >
                                                <Swatch source={k} />
                                                {SOURCE_META[k].label}
                                                <span className="cs2-lg-n">
                                                    {st.kind === 'ok' && fmtNum(filteredAll[k].length)}
                                                    {st.kind === 'loading' && 'loading…'}
                                                    {st.kind === 'error' && 'failed'}
                                                    {st.kind === 'empty' && 'none'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                    {ask != null && <span className="cs2-lg cs2-lg--line"><i className="cs2-line-ask" />Your ask</span>}
                                    {estimate != null && <span className="cs2-lg cs2-lg--line"><i className="cs2-line-est" />IronSight estimate</span>}
                                </div>
                                {series.length === 0 && anyLoading ? (
                                    <div className="cs2-chart cs2-chart--empty" role="status"><span className="cs2-chart-skel v2-sk" /><span className="u2-sub">Loading prices…</span></div>
                                ) : (
                                    <CompsScatter
                                        series={series}
                                        ask={ask}
                                        estimate={estimate}
                                        range={eff.range}
                                        now={now}
                                        selectedId={selectedId}
                                        onSelect={onSelect}
                                        label={chartLabel}
                                        meter={meter}
                                    />
                                )}
                                {older && (
                                    <p className="cs2-window" role="status">
                                        The newest {SOURCE_META[refSource].noun} is from {fmtDay(older.newest)}, older than this window.
                                        {' '}
                                        <button type="button" className="u2-linkb" onClick={() => setFilters((f) => ({ ...f, range: ALL_RANGE }))}>Show all dates</button>
                                    </p>
                                )}
                                {shownStates[refSource].kind === 'ok' && (
                                    <p className={`cs2-insight${info.small ? ' is-small' : ''}`} data-testid="comps-insight">
                                        {info.verdict && <Chip tone={info.verdict.tone}>{info.verdict.chip}</Chip>}
                                        <span>
                                            {info.sentence}
                                            {info.small && ' Small sample: treat as indicative.'}
                                            {info.ownSentence && ` ${info.ownSentence}`}
                                        </span>
                                    </p>
                                )}
                            </div>
                            <RecordList
                                sourceKey={view}
                                st={shownStates[view]}
                                fetched={fetchedAll[view]}
                                filtered={filteredAll[view]}
                                sort={sort}
                                setSort={setSort}
                                showAll={showAll}
                                setShowAll={setShowAll}
                                culprit={culprit}
                                older={older}
                                onRelax={(key) => setFilters((f) => relaxFilter(f, key))}
                                onReset={resetFilters}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                unitName={unitName}
                                ask={ask}
                                unitCtx={unitCtx}
                                meter={meter}
                                stock={stock}
                                buildId={fetchedAll.advertised.data?.build_id || undefined}
                            />
                        </div>
                    )}
                </div>
            </div>
            <HowToRead />
            <p className="u2-sub cs2-foot">{FOOTNOTE} Hours are {meterShort(meter) === 'h' ? 'engine hours' : meterLabel(meter).toLowerCase()} unless marked.</p>
        </section>
    );
}

CompsSourcesV2.propTypes = { unit: PropTypes.object.isRequired, machineCatalog: PropTypes.object };
